'use client';

import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useMemo,
  useState,
} from 'react';

export interface HotelContextState {
  bookingUrl?: string;
  currentStep: number;
  flightId?: number;
  isOpenConfigurationFlow: boolean;
  isOpenTravelPlan: boolean;
  isVisibleConfigurationFlow: boolean;
  parameters?: Content.Components['IHotelPageHeadlessParameters'];
  price?: number;
  productQueryParameter?: string;
  products: Content.Components['ProductSelectorProductDto'][];
  searchSettings?:
    | Content.Components['SearchSettingsDto']
    | Content.Components['TravelPortalSearchSettingsDto']
    | null;
  selectedRoom?: string | null;
  setBookingUrl: Dispatch<string | undefined>;
  setCurrentStep: Dispatch<SetStateAction<number>>;
  setCurrentStepSummary: (summary: string) => void;
  setFlightId: Dispatch<number | undefined>;
  setIsOpenConfigurationFlow: Dispatch<boolean>;
  setIsOpenTravelPlan: Dispatch<boolean>;
  setIsVisibleConfigurationFlow: Dispatch<boolean>;
  setParameters: Dispatch<
    SetStateAction<
      Content.Components['IHotelPageHeadlessParameters'] | undefined
    >
  >;
  setPrice: Dispatch<number | undefined>;
  setProductQueryParameter: Dispatch<string | undefined>;
  setProducts: Dispatch<Content.Components['ProductSelectorProductDto'][]>;
  setSearchSettings: Dispatch<
    | Content.Components['SearchSettingsDto']
    | Content.Components['TravelPortalSearchSettingsDto']
    | null
  >;
  setSelectedRoom: Dispatch<string | undefined>;
  setSpecialHotelType: Dispatch<
    Content.Components['SpecialHotelType'] | undefined
  >;
  setStepSummaries: Dispatch<string[]>;
  setUniqueId: Dispatch<string>;
  specialHotelType?: Content.Components['SpecialHotelType'];
  stepSummaries: string[];
  totalPrice?: number;
  uniqueId?: string;
  url?: string;
}

interface HotelContextProviderProps {
  children: ReactNode;
  initialData?: {
    specialHotelType?: Content.Components['SpecialHotelType'];
  } & Partial<HotelContextState>;
}

const HotelContext = createContext<HotelContextState | undefined>(undefined);

export const HotelContextProvider = ({
  children,
  initialData,
}: HotelContextProviderProps) => {
  const [parameters, setParameters] = useState<
    Content.Components['IHotelPageHeadlessParameters'] | undefined
  >(initialData?.parameters);
  const [bookingUrl, setBookingUrl] = useState(initialData?.bookingUrl);
  const [specialHotelType, setSpecialHotelType] = useState<
    Content.Components['SpecialHotelType'] | undefined
  >(initialData?.specialHotelType);
  const [productQueryParameter, setProductQueryParameter] = useState(
    initialData?.productQueryParameter,
  );
  const [currentStep, setCurrentStep] = useState(initialData?.currentStep ?? 1);
  const [flightId, setFlightId] = useState(initialData?.flightId);
  const [isOpenConfigurationFlow, setIsOpenConfigurationFlow] = useState(
    initialData?.isOpenConfigurationFlow ?? false,
  );
  const [isOpenTravelPlan, setIsOpenTravelPlan] = useState(
    initialData?.isOpenTravelPlan ?? false,
  );
  const [isVisibleConfigurationFlow, setIsVisibleConfigurationFlow] = useState(
    initialData?.isVisibleConfigurationFlow ?? false,
  );
  const [products, setProducts] = useState(initialData?.products ?? []);
  const [searchSettings, setSearchSettings] = useState<
    | Content.Components['SearchSettingsDto']
    | Content.Components['TravelPortalSearchSettingsDto']
    | null
  >();
  const [price, setPrice] = useState(initialData?.price);
  const [selectedRoom, setSelectedRoom] = useState(initialData?.selectedRoom);

  const [stepSummaries, setStepSummaries] = useState(
    initialData?.stepSummaries ?? [],
  );
  const [uniqueId, setUniqueId] = useState(initialData?.uniqueId);

  const setCurrentStepSummary = (summary: string) => {
    setStepSummaries(previousSummaries =>
      previousSummaries.map((stepSummary, stepSummaryIndex) =>
        stepSummaryIndex === currentStep - 1 ? summary : stepSummary,
      ),
    );
  };

  const totalPrice = useMemo(() => {
    if (typeof price !== 'number') return;

    let totalPrice = price;

    products.forEach(product => {
      if (!product.isIncluded && typeof product.totalPrice === 'number') {
        totalPrice += product.totalPrice;
      }
    });

    return totalPrice;
  }, [products, price]);

  const url = useMemo(() => {
    const baseUrl = bookingUrl ?? '';

    if (!productQueryParameter || products.length === 0) {
      return baseUrl;
    }

    const [path, queryString = ''] = baseUrl.split('?');
    const searchParams = new URLSearchParams(queryString);

    const productIds = products
      .map(product => product.id)
      .filter(id => id !== undefined && id !== null)
      .map(String);

    if (productIds.length > 0) {
      searchParams.delete(productQueryParameter);

      searchParams.set(productQueryParameter, productIds.join(','));
    }

    const query = searchParams.toString();

    return query ? `${path}?${query}` : path;
  }, [bookingUrl, productQueryParameter, products]);

  return (
    <HotelContext.Provider
      value={{
        bookingUrl,
        currentStep,
        flightId,
        isOpenConfigurationFlow,
        isOpenTravelPlan,
        isVisibleConfigurationFlow,
        parameters,
        price,
        productQueryParameter,
        products,
        searchSettings,
        selectedRoom,
        setBookingUrl,
        setCurrentStep,
        setCurrentStepSummary,
        setFlightId,
        setIsOpenConfigurationFlow,
        setIsOpenTravelPlan,
        setIsVisibleConfigurationFlow,
        setParameters,
        setPrice,
        setProductQueryParameter,
        setProducts,
        setSearchSettings,
        setSelectedRoom,
        setSpecialHotelType,
        setStepSummaries,
        setUniqueId,
        specialHotelType,
        stepSummaries,
        totalPrice,
        uniqueId,
        url,
      }}
    >
      {children}
    </HotelContext.Provider>
  );
};

export const useHotelContext = () => {
  const context = useContext(HotelContext);

  if (!context) {
    throw new Error(
      'useHotelContext must be used within a HotelContextProvider',
    );
  }

  return context;
};
